import React from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';
import { BarChart } from '@mui/x-charts/BarChart';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

function ContractDetail({ aaroQueries }) {
    const { orderDetailsQuery, contractQuery, movementsQuery, productMovementsQuery } = aaroQueries;
    // Para formatlama fonksiyonu
    const formatCurrency = (amount, contract) => {
        if (!contract || typeof amount !== 'number') return '₺0,00';
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: contract.DovizKodu || 'TRY',
        }).format(amount);
    };

    // Loading / Error Durumu
    if (contractQuery.isLoading || movementsQuery.isLoading || productMovementsQuery.isLoading) {
        return <MDProgress variant="gradient" />;
    }
    if (contractQuery.isError) {
        return <MDTypography color="error">Hata: {contractQuery.error.message}</MDTypography>;
    }
    if (movementsQuery.isError) {
        return <MDTypography color="error">Hata: {movementsQuery.error.message}</MDTypography>;
    }
    if (productMovementsQuery.isError) {
        return <MDTypography color="error">Hata: {productMovementsQuery.error.message}</MDTypography>;
    }

    // Veriler
    const contract =
        contractQuery.data && Array.isArray(contractQuery.data) && contractQuery.data.length > 0
            ? contractQuery.data[0]
            : {};

    // Sözleşme objesi kontrolü
    if (!contract || !contract.SozlesmeID) {
        return (
            <MDBox>
                <Card>
                    <CardContent>
                        <MDBox textAlign="center" py={4}>
                            <MDTypography variant="h6" color="text.secondary" mb={2}>
                                Geçersiz Sözleşme Verisi
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                                Sözleşme verisi eksik veya geçersiz.
                            </MDTypography>
                        </MDBox>
                    </CardContent>
                </Card>
            </MDBox>
        );
    }

    // İstenmeyen hareket tiplerini hariç tut
    const filteredMovements = (movementsQuery.data || []).filter(
        (row) =>
            ![
                5004, 5008, 5009, 10005, 10006, 10007, 10008, 10009, 10010, 10011, 10012, 10013, 10014, 10015, 10016,
                10019,
            ].includes(row.TipID)
    );

    // Toplam tahsilat
    const totalCollected = filteredMovements.reduce((sum, movement) => sum + movement.Tutar, 0);

    // Satış Faturası tutarı
    const satisFaturasiTutari =
        productMovementsQuery.data
            ?.filter((movement) => movement.TipAdi === 'Satış Faturası')
            .reduce((sum, movement) => sum + movement.Tutar, 0) * 1.2 || 0;
    // ! Vergiyi 20% olarak aldım fakat burası dinamik okunabilmeliydi

    // Sipariş detayları
    const orderDetails = orderDetailsQuery.data || [];
    // Toplam sipariş tutarı
    const toplamSiparisTutari = orderDetails.reduce((sum, detail) => sum + (detail.Tutar || 0), 0);

    // "Siparişe göre kesilmesi gereken fatura tutarı"
    // (Örnek: totalCollected - satisFaturasiTutari)

    // Bar Chart data
    const chartData = [
        {
            category: 'Sözleşme',
            value: contract?.Tutar || 0,
        },
        {
            category: 'Sipariş',
            value: toplamSiparisTutari || 0,
        },
        {
            category: 'Tahsilat',
            value: totalCollected || 0,
        },
        {
            category: 'Fatura',
            value: satisFaturasiTutari || 0,
        },
    ];

    // Chart'ın render edilip edilmeyeceğini kontrol et
    const shouldRenderChart = chartData.some((item) => item.value > 0);

    return (
        <MDBox>
            <Card>
                <CardContent>
                    {/* Contract Summary Info */}
                    <MDBox mb={3} lineHeight={1.5}>
                        <MDTypography
                            variant="h6"
                            fontWeight="medium"
                            component="a"
                            href={`https://erp.aaro.com.tr/Sozlesme/Kalem?id=${contract.SozlesmeID || ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {contract.SozlesmeAdi?.toUpperCase() || 'Sözleşme Adı Belirtilmemiş'}
                        </MDTypography>
                        <br />
                        <MDTypography variant="caption" fontWeight="text" lineHeight={0}>
                            {contract.CariAdi?.toUpperCase() || 'Cari Adı Belirtilmemiş'}
                        </MDTypography>
                        <br />
                        <MDTypography variant="caption" fontWeight="text" lineHeight={0}>
                            Sözleşme Tutarı: {formatCurrency(contract.Tutar || 0, contract)}
                        </MDTypography>
                        <br />
                        <MDTypography variant="caption" fontWeight="text" lineHeight={0}>
                            Oluşturma:{' '}
                            {contract.Tarih
                                ? new Date(contract.Tarih).toLocaleDateString('tr-TR')
                                : 'Tarih Belirtilmemiş'}
                            {contract.Tarih &&
                                ` (${Math.floor(
                                    (new Date() - new Date(contract.Tarih)) / (1000 * 60 * 60 * 24)
                                )} gün önce`}
                            {contract.OlsAdi ? ` ${contract.OlsAdi.toLowerCase()} tarafından` : ''})
                        </MDTypography>
                    </MDBox>
                </CardContent>
            </Card>
            {/* Chart Section */}
            <Card sx={{ mt: 3 }}>
                {' '}
                {/* mt: 3 adds margin-top for spacing */}
                <MDBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', m: 3 }}>
                    {shouldRenderChart ? (
                        <MDBox
                            sx={{
                                width: '100%',
                                height: 350,
                                overflow: 'hidden',
                                '& .MuiBarElement-root:nth-of-type(1)': {
                                    fill: '#1A73E8 !important',
                                },
                                '& .MuiBarElement-root:nth-of-type(2)': {
                                    fill: '#344767 !important',
                                },
                                '& .MuiBarElement-root:nth-of-type(3)': {
                                    fill: '#4CAF50 !important',
                                },
                                '& .MuiBarElement-root:nth-of-type(4)': {
                                    fill: '#fb8c00 !important',
                                },
                            }}
                        >
                            <BarChart
                                xAxis={[
                                    {
                                        scaleType: 'band',
                                        data: ['Sözleşme', 'Sipariş', 'Tahsilat', 'Fatura'],
                                        tickLabelStyle: {
                                            angle: 0,
                                            textAnchor: 'middle',
                                            fontSize: 12,
                                            fontWeight: 500,
                                        },
                                    },
                                ]}
                                yAxis={[
                                    {
                                        valueFormatter: (value) => formatCurrency(value, contract),
                                    },
                                ]}
                                series={[
                                    {
                                        data: [
                                            contract?.Tutar || 0,
                                            toplamSiparisTutari || 0,
                                            totalCollected || 0,
                                            satisFaturasiTutari || 0,
                                        ],
                                        label: 'Tutar',
                                        valueFormatter: (value) => formatCurrency(value, contract),
                                    },
                                ]}
                                height={350}
                                slotProps={{
                                    legend: { hidden: true },
                                }}
                                margin={{
                                    left: 10,
                                    right: 0,
                                    top: 40,
                                    bottom: 30,
                                }}
                            />
                        </MDBox>
                    ) : (
                        <MDBox textAlign="center" py={4}>
                            <MDTypography variant="body2" color="text.secondary">
                                Grafik için yeterli veri bulunmuyor.
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
            </Card>
        </MDBox>
    );
}

export default ContractDetail;
